/**
 * Offset added to an HTTP status to make the WebSocket close code a refused agent connection is
 * closed with — 4403 for a 403, 4404 for a 404 — in the range the protocol leaves to applications.
 *
 * A browser hides the status of a failed WebSocket handshake from the page: answered with a plain
 * 403, the client sees a socket that did not open — which is what a network blip looks like — and
 * retries it on every backoff for as long as the tab stays open. Accepted and closed at once, the
 * socket carries the status as a close code the client can read, and stop on.
 */
export const AGENT_REJECTION_CLOSE_CODE_OFFSET = 4000;

/**
 * Longest close reason the protocol allows, counted in bytes rather than characters.
 */
const MAX_CLOSE_REASON_BYTES = 123;

/**
 * Statuses that answer a *credential*, not the request: a retry mints a fresh token and can
 * legitimately succeed, so they are not final. 401 covers an expired token as much as an invalid
 * one, and 400 is what a connection carrying no token at all gets.
 */
const RENEWABLE_STATUS_CODES = new Set([400, 401]);

/**
 * Statuses that answer the *moment*: the same request is worth sending again shortly.
 */
const TRANSIENT_STATUS_CODES = new Set([408, 409, 429]);

/**
 * Close code for a connection refused with the given HTTP status.
 */
export function getAgentRejectionCloseCode(statusCode: number): number {
    return AGENT_REJECTION_CLOSE_CODE_OFFSET + statusCode;
}

/**
 * The HTTP status a connection was refused with, read back from its close code — or null when the
 * socket closed for any other reason.
 */
export function getAgentRejectionStatusCode(closeCode: number): number | null {
    const statusCode = closeCode - AGENT_REJECTION_CLOSE_CODE_OFFSET;
    return statusCode >= 400 && statusCode <= 599 ? statusCode : null;
}

/**
 * Whether a refusal is final for the client that got it: the same request would be refused the
 * same way. Being throttled, timing out or losing a race is not, the server failing is not, and
 * neither is a credential the client can renew by asking again.
 */
export function isFinalAgentRejection(statusCode: number): boolean {
    return (
        statusCode >= 400 &&
        statusCode <= 499 &&
        !TRANSIENT_STATUS_CODES.has(statusCode) &&
        !RENEWABLE_STATUS_CODES.has(statusCode)
    );
}

/**
 * Whether reconnecting with newly minted credentials can resolve this refusal.
 */
export function isRenewableAgentRejection(statusCode: number): boolean {
    return RENEWABLE_STATUS_CODES.has(statusCode);
}

/**
 * Fit a close reason within the protocol's limit.
 */
export function truncateCloseReason(reason: string): string {
    const bytes = new TextEncoder().encode(reason);
    if (bytes.byteLength <= MAX_CLOSE_REASON_BYTES) {
        return reason;
    }

    const ellipsis = '\u2026';
    const budget = MAX_CLOSE_REASON_BYTES - new TextEncoder().encode(ellipsis).byteLength;
    // Cut on a UTF-8 lead byte, so the tail is never half a character: continuation bytes are
    // 0b10xxxxxx, and walking back off them lands on the start of the character they belong to.
    let end = budget;
    while (end > 0 && (bytes[end] & 0xc0) === 0x80) {
        end -= 1;
    }

    return new TextDecoder().decode(bytes.subarray(0, end)) + ellipsis;
}
